import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';

import { Input } from '@/components/ui/inputs/input';
import InputRadio from '@/components/ui/inputs/input-radio';
import SidebarDrawer from '@/components/common/SidebarDrawer';
import ToastMe from '@/components/ui/ToastMe';
import validationErrors from '@/lib/validationErrors';

import {
  addCustomerService,
  updateCustomerService,
  getByIdCustomerService,
  changeCustomerPasswordService, // ← add this import
} from '@/services/CustomerServices';

const AddEditDrawer = ({
  titleText,
  activeDrawer,
  setActiveDrawer,
  getTableData,
  editDataId = '',
}) => {
  const dispatch = useDispatch();

  const {
    setValue,
    getValues,
    watch,
    formState: { errors },
    handleSubmit,
    control,
    reset,
  } = useForm();

  const [itemStatus, setItemStatus] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isFormSubmit, setIsFormSubmit] = useState(false);

  // watch fields
  const creditEnabledValue = watch('creditEnabled', false);

  /* ---------------- init/reset ---------------- */
  useEffect(() => {
    formReset();
  }, []);

  useEffect(() => {
    formReset();
    if (editDataId) {
      getDataById(editDataId);
    }
  }, [editDataId]);

  const formReset = async () => {
    try {
      const formData = getValues();
      for (const key in formData) setValue(key, '');
      reset();

      // defaults
      setValue('status', '1');
      setValue('creditEnabled', false);
      setValue('creditLimit', 0);
      setValue('outstandingCredit', 0);

      setItemStatus(1);
    } catch (e) {
      console.error('[CustomerDrawer] reset failed', e);
    }
  };

  const handleDrawerClose = () => {
    formReset();
    setActiveDrawer(false);
    document.body.style.overflowY = 'auto';
    getTableData?.();
  };

  const handleChangeStatus = (event) => {
    const v = Number(event?.target?.value ?? 1);
    setValue('status', v);
    setItemStatus(v);
  };

  /* ---------------- load for edit ---------------- */
  const getDataById = (id) => {
    setIsLoading(true);
    dispatch(getByIdCustomerService(id))
      .then((res) => {
        const formData = res?.data?.data || {};
        // hydrate safe fields
        Object.keys(formData).forEach((k) => {
          const v = formData[k];
          if (v === undefined || v === null) return setValue(k, '');
          setValue(k, v);
        });

        // fix types
        setValue('creditEnabled', !!formData.creditEnabled);
        setValue('creditLimit', Number(formData.creditLimit || 0));
        setValue('outstandingCredit', Number(formData.outstandingCredit || 0));
        setValue('status', Number(formData.status || 1));
        setItemStatus(Number(formData.status || 1));

        // never prefill passwords
        setValue('password', '');
        setValue('confirm_password', '');

        setIsLoading(false);
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsLoading(false);
      });
  };

  /* ---------------- save (profile → then optional password reset) ---------------- */
  const onSubmit = (data) => {
    // ----- 1) password rules -----
    const hasPw = !!data.password;

    if (editDataId) {
      if (hasPw && data.password !== data.confirm_password) {
        return ToastMe('Password and confirm password not same', 'error');
      }
    } else {
      if (!data.password || !data.confirm_password) {
        return ToastMe('Password and confirm password are required', 'error');
      }
      if (data.password !== data.confirm_password) {
        return ToastMe('Password and confirm password not same', 'error');
      }
    }

    // ----- 2) coerce / validate credit fields -----
    const creditEnabled   = data.creditEnabled === true || data.creditEnabled === 'true';
    const status          = Number(data.status || 1);
    const creditLimit     = Number(data.creditLimit ?? 0);
    const outstandingCred = Number(data.outstandingCredit ?? 0);

    if (!creditEnabled) {
      data.creditLimit = 0;
      data.outstandingCredit = 0;
    } else {
      if (creditLimit < 0 || outstandingCred < 0) {
        return ToastMe('Credit values must be >= 0', 'error');
      }
      if (outstandingCred > creditLimit) {
        return ToastMe('Outstanding credit cannot exceed credit limit', 'error');
      }
      data.creditLimit = creditLimit;
      data.outstandingCredit = outstandingCred;
    }

    data.creditEnabled = creditEnabled;
    data.status = status;

    // ----- 3) split payloads -----
    const { password, confirm_password, ...profileOnly } = data;

    setIsFormSubmit(true);

    // CREATE → backend expects password in the same request
    if (!editDataId) {
      return dispatch(addCustomerService(data))
        .then(() => {
          setIsFormSubmit(false);
          handleDrawerClose();
        })
        .catch(({ message, errorData }) => {
          validationErrors(errorData, message);
          setIsFormSubmit(false);
        });
    }

    // UPDATE → profile first, then password (admin reset) if provided
    const doProfile  = () => dispatch(updateCustomerService(editDataId, profileOnly));
    const doPassword = () => {
      if (!password) return Promise.resolve(); // no password change
      return dispatch(
        changeCustomerPasswordService({
          password,
          customerIdForAdminFallback: editDataId, // admin path: PUT /customer/:id/change-password
        })
      );
    };

    doProfile()
      .then(() => doPassword())
      .then(() => {
        setIsFormSubmit(false);
        // clear password fields locally
        setValue('password', '');
        setValue('confirm_password', '');
        handleDrawerClose();
      })
      .catch(({ message, errorData }) => {
        validationErrors(errorData, message);
        setIsFormSubmit(false);
      });
  };

  const disabled = isLoading || isFormSubmit;

  return (
    <SidebarDrawer
      icon="lab lab-add-circle-line"
      titleText={titleText}
      activeDrawer={activeDrawer}
      handleDrawerClose={handleDrawerClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Row 1: Name / Email */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Controller
            control={control}
            rules={{ required: true, minLength: 2, maxLength: 50 }}
            name="name"
            render={({ field }) => (
              <Input {...field} size="sm" labelText="Name" type="text" disabled={disabled}
                     errorData={{ required: 'Required', minLength: '2', maxLength: '50' }}
                     errorType={errors?.name?.type} />
            )}
          />
          <Controller
            control={control}
            rules={{ required: true, pattern: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/ }}
            name="email"
            render={({ field }) => (
              <Input {...field} size="sm" labelText="Email" type="text" disabled={disabled}
                     errorData={{ required: 'Required', pattern: 'Invalid Email' }}
                     errorType={errors?.email?.type} />
            )}
          />
        </div>

        {/* Row 2: Phone */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Controller
            control={control}
            rules={{ required: true, minLength: 7, maxLength: 20 }}
            name="phone_no"
            render={({ field }) => (
              <Input {...field} size="sm" labelText="Phone" type="text" disabled={disabled}
                     errorData={{ required: 'Required', minLength: '7', maxLength: '20' }}
                     errorType={errors?.phone_no?.type} />
            )}
          />
          <div />
        </div>

        {/* Row 3: Credit */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Controller
            control={control}
            rules={{ min: 0 }}
            name="creditLimit"
            render={({ field }) => (
              <Input {...field} size="sm" type="number" labelText="Credit Limit" disabled={disabled}
                     errorData={{ min: '>= 0' }} errorType={errors?.creditLimit?.type} />
            )}
          />
          <Controller
            control={control}
            rules={{ min: 0 }}
            name="outstandingCredit"
            render={({ field }) => (
              <Input {...field} size="sm" type="number" labelText="Outstanding Credit" disabled={disabled}
                     errorData={{ min: '>= 0' }} errorType={errors?.outstandingCredit?.type} />
            )}
          />
        </div>

        {/* Row 4: Password */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Controller
            control={control}
            rules={editDataId ? { minLength: 6, maxLength: 50 } : { required: true, minLength: 6, maxLength: 50 }}
            name="password"
            render={({ field }) => (
              <Input {...field} type="text" labelText={editDataId ? 'New Password' : 'Password'}
                     autoComplete="off" disabled={disabled}
                     errorData={{ required: 'Required', minLength: '6', maxLength: '50' }}
                     errorType={errors?.password?.type} />
            )}
          />
          <Controller
            control={control}
            rules={editDataId ? { minLength: 6, maxLength: 50 } : { required: true, minLength: 6, maxLength: 50 }}
            name="confirm_password"
            render={({ field }) => (
              <Input {...field} type="text" labelText="Confirm Password"
                     autoComplete="off" disabled={disabled}
                     errorData={{ required: 'Required', minLength: '6', maxLength: '50' }}
                     errorType={errors?.confirm_password?.type} />
            )}
          />
        </div>

        {/* Row 5: Status / Credit toggle */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="form-input-item">
            <label className="db-field-title required" htmlFor="active">Status</label>
            <div className="db-field-radio-group">
              <InputRadio
                id="active"
                name="status"
                labelText="Active"
                value="1"
                onChange={() => handleChangeStatus({ target: { value: 1 } })}
                checked={Number(itemStatus) === 1}
                disabled={disabled}
              />
              <InputRadio
                id="inactive"
                name="status"
                labelText="Inactive"
                value="2"
                onChange={() => handleChangeStatus({ target: { value: 2 } })}
                checked={Number(itemStatus) === 2}
                disabled={disabled}
              />
            </div>
          </div>
          <div className="form-input-item">
            <label className="db-field-title">Enable Credit</label>
            <div className="db-field-radio-group">
              <InputRadio
                id="creditYes"
                name="creditEnabled"
                labelText="Yes"
                value="true"
                onChange={() => setValue('creditEnabled', true)}
                checked={creditEnabledValue === true}
                disabled={disabled}
              />
              <InputRadio
                id="creditNo"
                name="creditEnabled"
                labelText="No"
                value="false"
                onChange={() => setValue('creditEnabled', false)}
                checked={creditEnabledValue === false}
                disabled={disabled}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="form-col-12">
          <div className="flex flex-wrap gap-3 mt-4">
            <button type="submit" className="db-btn py-2 text-white bg-primary"
                    disabled={disabled}>
              <i className="lab lab-save" />
              <span>Save</span>
              {isFormSubmit && (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                     xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10"
                          stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
            </button>
            <button type="button" className="modal-btn-outline modal-close" onClick={handleDrawerClose} disabled={disabled}>
              <i className="lab lab-close" />
              <span>Close</span>
            </button>
          </div>
        </div>
      </form>
    </SidebarDrawer>
  );
};

export default AddEditDrawer;
